<script setup lang="ts">
type DataRequestData = App.DataRequests.Data.DataRequestData;
type EnumData = App.Base.Data.EnumData;
type FinancialAidData = App.Students.Data.FinancialAidData;

import type { HybridlyFormData } from '@/@types/global';
import type { Selectable } from '@/components/selectBox/selectBox';

import { useDataRequestIndicators } from '@/hooks/useDataRequestIndicators';

import Alert from '@/components/alert/Alert.vue';
import FormDateInput from '@/components/formDateInput/FormDateInput.vue';
import FormListboxInput from '@/components/formListboxInput/FormListboxInput.vue';
import FormRadioInput from '@/components/formRadioInput/FormRadioInput.vue';
import FormRows from '@/components/formRows/FormRows.vue';
import FormTextareaInput from '@/components/formTextareaInput/FormTextareaInput.vue';

import TimesCircle from '@/icons/line/times-circle.svg';

const props = defineProps<{
  dataRequest?: DataRequestData;
  form: HybridlyFormData<{
    financial_aid: {
      status: EnumData | null;
      date_submitted: string | null;
      css_profile: EnumData | null;
      pell_eligible: boolean | null | EnumData;
      selected_for_verification_step_one: string | null;
      selected_for_verification_step_two: string | null;
    };
    user_comment: string;
  }>;
  financialAid: FinancialAidData;
  statuses: EnumData[];
  studentCssProfiles: EnumData[];
  selectedForVerificationOptions: EnumData[];
}>();

const { computedStatusBadge, hasAttributeChange } = useDataRequestIndicators({
  dataRequest: props.dataRequest,
  base: props.financialAid
});

const canShowCssProfile = computed(() => {
  const statusValue = props.form.fields.financial_aid?.status?.value;
  const hasStatusDate = !!props.form.fields.financial_aid?.date_submitted;
  return (statusValue === 'submitted' || statusValue === 'completed') && hasStatusDate;
});

const selectedForVerificationOptionsStepOne = [
  {
    label: 'No or not sure',
    value: 'unknown'
  },
  {
    label: "Yes, I've been selected",
    value: 'yes-selected'
  }
];

const selectedForVerificationOptionsStepTwo = [
  {
    label: 'No, not yet',
    value: 'yes-info-unknown'
  },
  {
    label: 'Yes, submitted',
    value: 'yes-submitted'
  },
  {
    label: 'Yes, submitted and verification was successful',
    value: 'yes-successful'
  }
];

function handleStatusChange(status: Selectable<string> | null) {
  if (!status) {
    return;
  }

  props.form.fields.financial_aid.status = status;

  if (status.value !== props.form.initial.financial_aid.status?.value) {
    props.form.fields.financial_aid.date_submitted = null;
    return;
  }

  props.form.fields.financial_aid.date_submitted = props.form.initial.financial_aid.date_submitted;
}
</script>

<template>
  <div class="-mx-5 -mb-8 -mt-6 flex flex-col divide-y divide-slate-200">
    <div class="px-5 pb-5 pt-6" v-if="dataRequest?.status.value === 'rejected'">
      <Alert
        v-if="dataRequest?.status.value === 'rejected'"
        :description="`Reason: ${dataRequest.rejected_reason ?? 'No reason provided'}`"
        :icon="TimesCircle"
        title="Submission was not approved"
        color="danger"
        variant="soft"
      />
    </div>
    <FormRows class="px-5 pb-8 pt-6">
      <FormListboxInput
        :error="form.errors.financial_aid?.status"
        :modelValue="form.fields.financial_aid.status"
        :options="statuses"
        :statusBadgeColor="computedStatusBadge.color"
        :statusBadgeIcon="computedStatusBadge.icon"
        :statusBadgeLabel="hasAttributeChange('status') ? computedStatusBadge.label : undefined"
        label="FAFSA status"
        @update:modelValue="handleStatusChange"
      />
      <FormDateInput
        :error="form.errors.financial_aid?.date_submitted"
        :modelValue="form.fields.financial_aid.date_submitted"
        helperText="This is the date you submitted your FAFSA."
        :statusBadgeColor="computedStatusBadge.color"
        :statusBadgeIcon="computedStatusBadge.icon"
        :statusBadgeLabel="
          hasAttributeChange('date_submitted') ? computedStatusBadge.label : undefined
        "
        label="FAFSA status date"
        name="date_submitted"
        format="MMM dd, yyyy"
        :clearable="false"
        @update:modelValue="(value) => (form.fields.financial_aid.date_submitted = value)"
      />
      <FormListboxInput
        v-if="canShowCssProfile"
        :error="form.errors.financial_aid?.css_profile"
        :modelValue="form.fields.financial_aid.css_profile"
        :options="studentCssProfiles"
        :statusBadgeColor="computedStatusBadge.color"
        :statusBadgeIcon="computedStatusBadge.icon"
        :statusBadgeLabel="
          hasAttributeChange('css_profile') ? computedStatusBadge.label : undefined
        "
        isOptional
        label="CSS profile"
        @update:modelValue="(value) => (form.fields.financial_aid.css_profile = value)"
      />
      <FormRadioInput
        label="Has your FAFSA been selected for verification?"
        :error="form.errors.financial_aid?.selected_for_verification"
        :isFieldset="false"
        :radioInputs="selectedForVerificationOptionsStepOne"
        :statusBadgeColor="computedStatusBadge.color"
        :statusBadgeIcon="computedStatusBadge.icon"
        :statusBadgeLabel="
          hasAttributeChange('selected_for_verification') ? computedStatusBadge.label : undefined
        "
        :value="form.fields.financial_aid.selected_for_verification_step_one"
        ariaLabel="Selected for verification"
        name="selected_for_verification"
        variant="outlined"
        @update:modelValue="
          (value) => (form.fields.financial_aid.selected_for_verification_step_one = value)
        "
      />
      <FormRadioInput
        v-if="
          form.fields.financial_aid.selected_for_verification_step_one !== 'unknown' &&
          !!form.fields.financial_aid.selected_for_verification_step_one
        "
        label="Have you submitted the requested information?"
        :isFieldset="false"
        :radioInputs="selectedForVerificationOptionsStepTwo"
        :statusBadgeColor="computedStatusBadge.color"
        :statusBadgeIcon="computedStatusBadge.icon"
        :value="form.fields.financial_aid.selected_for_verification_step_two"
        :statusBadgeLabel="
          hasAttributeChange('selected_for_verification') ? computedStatusBadge.label : undefined
        "
        ariaLabel="Selected for verification"
        name="selected_for_verification"
        variant="outlined"
        @update:modelValue="
          (value) => (form.fields.financial_aid.selected_for_verification_step_two = value)
        "
      />
      <FormRadioInput
        label="Are you eligible to receive the Pell Grant based on your student aid index (SAI)?"
        :error="form.errors.financial_aid?.pell_eligible"
        :isFieldset="false"
        :radioInputs="[
          {
            label: 'Eligible',
            value: true
          },
          {
            label: 'Not eligible',
            value: false
          },
          {
            label: 'Not sure',
            value: null
          }
        ]"
        :statusBadgeColor="computedStatusBadge.color"
        :statusBadgeIcon="computedStatusBadge.icon"
        :statusBadgeLabel="
          hasAttributeChange('pell_eligible') ? computedStatusBadge.label : undefined
        "
        :value="form.fields.financial_aid.pell_eligible"
        ariaLabel="Pell eligibility"
        name="pell_eligible"
        variant="outlined"
        @update:modelValue="(value) => (form.fields.financial_aid.pell_eligible = value)"
      />
    </FormRows>
    <FormRows class="px-5 pb-8 pt-6">
      <FormTextareaInput
        label="Note"
        helpText="Note to adviser about this submission"
        :error="form.errors.user_comment"
        :modelValue="form.fields.user_comment"
        name="user_comment"
        isOptional
        @update:modelValue="(value) => (form.fields.user_comment = value)"
      />
    </FormRows>
  </div>
</template>
